import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { CreateProjectModel, CustomProjectModel, ProjectType } from 'core/domain/project/model/projectModel';
import { DefaultOptionType } from 'antd/lib/select';
import {
  TABLE_PAGINATION_PAGE_DEFAULT,
  TABLE_PAGINATION_SIZE_ALL_RESULTS,
  TABLE_PAGINATION_SIZE_DEFAULT,
  TablePaginationSearchValue,
} from 'constants/table';
import { getProjectPathById } from 'components/pages/App/routes/projects/config';
import { ProjectTab } from 'models/project.model';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { GetProjectsListByClientIdWithPagination } from 'core/domain/project/repositories/getProjectsListByClientIdWithPagination';
import { getClientTabListWithPaginationPath } from 'components/pages/App/routes/settings/config';
import { useProfileUser } from 'hooks/useProfileUser';
import { UserRoleType } from 'models/users.model';
import { ProjectCreateFormDataProps, ProjectCreateFormValues } from 'components/molecules/ProjectCreateForm';
import { CreateProject } from 'core/domain/project/repositories/createProject';
import { MessageErrorModel, useMessage } from 'hooks/useMessage';
import { getDeleteProjectErrorText, projectTypeTranslationManager } from 'utils/projects';
import { UserVerifierModel } from 'core/domain/users/models';
import { GetUserVerifierListByClientId } from 'core/domain/users/repositories/getUserVerifierListByClientId';
import { checkEmailListErrors, EMAIL_LIST_SEPARATORS } from 'utils/email';
import { ProjectsColumns } from 'components/organisms/ProjectListColumns';
import { DELAY_MESSAGE_TIME } from 'constants/time';
import { DeleteProject } from 'core/domain/project/repositories/deleteProject';
import { ClientTab } from '../../resources/models';

const DEFAULT_PAGE_NUMBER = Number(TABLE_PAGINATION_PAGE_DEFAULT);
const DEFAULT_PAGE_SIZE_NUMBER = Number(TABLE_PAGINATION_SIZE_DEFAULT);

export const useClientProjectsList = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { t } = useTranslation();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const history = useHistory();
  const { data: userData } = useProfileUser();
  const { setMessageError, setMessageSuccess } = useMessage();
  const page = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.PAGE);
  const size = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.SIZE);
  const [projects, setProjects] = useState<CustomProjectModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [currentPage, setCurrentPage] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [pageSize, setPageSize] = useState<number>(Number(TABLE_PAGINATION_SIZE_DEFAULT));
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isCreateProjectModalVisible, setIsCreateProjectModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm<ProjectCreateFormValues>();
  const [isCreatingProject, setIsCreatingProject] = useState<boolean>(false);
  const [isDeleteProjectModalVisible, setIsDeleteProjectModalVisible] = useState<boolean>(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string>('');
  const [isDeletingProject, setIsDeletingProject] = useState<boolean>(false);
  const [userVerifierList, setUserVerifierList] = useState<DefaultOptionType[]>([]);
  const [isLoadingUserVerifierList, setIsLoadingUserVerifierList] = useState<boolean>(false);
  const [isUsersRequireVerificationChecked, setIsUsersRequireVerificationChecked] = useState<boolean>(false);

  const isUserSuperAdmin = !!userData?.roles.includes(UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN);

  const translations = {
    projects: t('projects'),
    searchPlaceholder: t('search_with_ellipsis'),
    createProjectButtonText: t('_PROJECTS_LIST_CREATE_PROJECT_BUTTON_TEXT'),
  };

  const createProjectModalTranslations = {
    title: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_TITLE_PLACEHOLDER'),
    actionButton: t('create'),
    cancelButton: t('cancel'),
  };

  const createProjectFormTranslations = {
    nameLabel: t('name'),
    namePlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_NAME_PLACEHOLDER'),
    nameError: t('required'),
    supportEmailsLabel: t('support_email'),
    supportEmailsPlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_SUPPORT_EMAIL_LIST_PLACEHOLDER'),
    usersVerifiersLabel: t('registration_validator_user_info'),
    usersVerifiersPlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_USER_VERIFIER_LIST_PLACEHOLDER'),
    usersRequireVerificationLabel: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_USER_VERIFIER_LIST_LABEL'),
    typeLabel: t('_PROJECT_TYPE'),
    typeError: t('required'),
    typePlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_TYPE_PLACEHOLDER'),
    clientLabel: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_CLIENT_LABEL'),
    clientPlaceholder: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_CLIENT_PLACEHOLDER'),
    clientError: t('required'),
    userVerifierListError: t('required'),
  };

  const deleteProjectModalTranslations = {
    title: t('_DELETE_PROJECT_MODAL_TITLE'),
    text: t('_DELETE_PROJECT_MODAL_TEXT'),
    mainButtonText: t('delete'),
    secondaryButtonText: t('cancel'),
  };

  const columnTranslations = {
    nameTitle: t('name'),
    typeTitle: t('type'),
    supportEmailTitle: t('support_email'),
    actionsTitle: t('actions'),
    actionsDeleteProjectButtonText: t('_PROJECTS_LIST_DELETE_PROJECT_TEXT'),
    notReportedText: t('not_reported'),
  };

  const getProjectsListByClientIdWithPagination = async ({
    page,
    size,
    search,
  }: {
    page: number;
    size: number;
    search?: string;
  }): Promise<void> => {
    setIsLoading(true);
    try {
      const { data, meta } = await GetProjectsListByClientIdWithPagination({
        host,
        token,
        clientId,
        pageNumber: page,
        pageSize: size,
        search,
      });
      setProjects(data);
      setTotalPages(meta.totalPages);
    } catch (error) {
      setMessageError({ description: t('_PROJECTS_LIST_ERROR_MESSAGE') });
    } finally {
      setIsLoading(false);
    }
  };

  const onChangePage = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    const parsedPageSize = pageSize || Number(TABLE_PAGINATION_SIZE_DEFAULT);
    setPageSize(parsedPageSize);
    history.push(
      getClientTabListWithPaginationPath({ clientId, tab: ClientTab.PROJECTS, page: String(page), size: String(parsedPageSize) })
    );
  };

  const onSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  };

  const onSelectProject = (project: CustomProjectModel, tab?: ProjectTab) => {
    history.push(getProjectPathById({ projectId: project.id, tab }));
  };

  const onOpenCreateProjectModal = () => {
    setIsCreateProjectModalVisible(true);
  };

  const onCloseCreateProjectModal = () => {
    setIsCreateProjectModalVisible(false);
    form.resetFields();
    setUserVerifierList([]);
    setIsUsersRequireVerificationChecked(false);
  };

  const onCreateProjectFailed = () => {
    setIsCreatingProject(false);
  };

  const getTypeOptions = () => {
    return Object.values(ProjectType).map((option) => {
      return { label: projectTypeTranslationManager[option], value: option };
    });
  };

  const onResetPage = () => {
    setCurrentPage(DEFAULT_PAGE_NUMBER);
    setPageSize(DEFAULT_PAGE_SIZE_NUMBER);
    getProjectsListByClientIdWithPagination({
      page: Number(page) || Number(TABLE_PAGINATION_PAGE_DEFAULT),
      size: Number(size) || Number(TABLE_PAGINATION_SIZE_DEFAULT),
      search: searchQuery,
    });
  };

  const onCreateProject = async (values: ProjectCreateFormValues) => {
    const { name, supportEmails, type, usersRequireVerification, usersVerifiers } = values;
    try {
      setIsCreatingProject(true);
      const data: CreateProjectModel = {
        clientId,
        name,
        supportEmail: supportEmails,
        type,
        usersRequireVerification: usersRequireVerification || false,
        usersVerifiers,
      };
      await CreateProject({ host, token, data });
      onResetPage();
      setMessageSuccess({ description: t('_PROJECTS_LIST_CREATE_PROJECT_FORM_SUCCESS_MESSAGE') });
      onCloseCreateProjectModal();
    } catch (error) {
      setMessageError({ description: t('_PROJECTS_LIST_CREATE_PROJECT_ERROR_MESSAGE') });
    } finally {
      setIsCreatingProject(false);
    }
  };

  const parseUserVerifierList = (userList: UserVerifierModel[]) => {
    return userList.map(({ userEmail, userId }) => ({ label: userEmail, value: userId }));
  };

  const getUserVerifierList = async (id: string) => {
    try {
      setIsLoadingUserVerifierList(true);
      const { data } = await GetUserVerifierListByClientId({
        host,
        token,
        clientId: id,
        page: Number(TABLE_PAGINATION_PAGE_DEFAULT),
        size: Number(TABLE_PAGINATION_SIZE_ALL_RESULTS),
      });
      const parsedUserVerifierList = parseUserVerifierList(data.userList);
      setUserVerifierList(parsedUserVerifierList);
    } catch (error) {
      setMessageError({ description: t('_PROJECTS_LIST_USER_VERIFIER_LIST_ERROR_MESSAGE') });
    } finally {
      setIsLoadingUserVerifierList(false);
    }
  };

  const onChangeUserVerificationCheck = (value: boolean) => {
    setIsUsersRequireVerificationChecked(value);
  };

  const createProjectFormData: ProjectCreateFormDataProps = {
    allowedUsersToVerifyAccounts: userVerifierList,
    form,
    loading: isCreatingProject,
    onFinish: onCreateProject,
    onFinishFailed: onCreateProjectFailed,
    onChangeUserVerificationCheck,
    translations: createProjectFormTranslations,
    typeOptions: getTypeOptions(),
    validateEmailFormat: checkEmailListErrors,
    supportEmailsSeparators: EMAIL_LIST_SEPARATORS,
    isLoadingUserVerifierList,
    isUsersRequireVerificationChecked,
  };

  const onOpenDeleteProjectModal = (id: string) => {
    setIsDeleteProjectModalVisible(true);
    setDeleteProjectId(id);
  };

  const onCloseDeleteProjectModal = () => {
    setIsDeleteProjectModalVisible(false);
    setDeleteProjectId('');
  };

  const onDeleteProject = async () => {
    try {
      setIsDeletingProject(true);
      await DeleteProject({ host, token, projectId: deleteProjectId });
      setMessageSuccess({ description: t('_PROJECTS_LIST_DELETE_PROJECT_FORM_SUCCESS_MESSAGE') });
      onResetPage();
      onCloseDeleteProjectModal();
    } catch (error) {
      const description = getDeleteProjectErrorText(error as MessageErrorModel);
      setMessageError({ description }, DELAY_MESSAGE_TIME.MEDIUM);
    } finally {
      setIsDeletingProject(false);
    }
  };

  const projectsColumns = ProjectsColumns({
    translations: columnTranslations,
    onOpenDeleteProjectModal,
    isUserSuperAdmin,
  });

  useEffect(() => {
    const newPage = page || TABLE_PAGINATION_PAGE_DEFAULT;
    const newSize = size || TABLE_PAGINATION_SIZE_DEFAULT;
    setCurrentPage(Number(newPage));
    setPageSize(Number(newSize));
  }, [page, size]);

  useEffect(() => {
    getProjectsListByClientIdWithPagination({
      page: Number(page) || Number(TABLE_PAGINATION_PAGE_DEFAULT),
      size: Number(size) || Number(TABLE_PAGINATION_SIZE_DEFAULT),
      search: searchQuery,
    });
    getUserVerifierList(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, searchQuery]);

  useEffect(() => {
    getUserVerifierList(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return {
    projects,
    projectsColumns,
    totalPages,
    currentPage,
    pageSize,
    translations,
    createProjectFormData,
    createProjectModalTranslations,
    deleteProjectModalTranslations,
    onChangePage,
    onSearch,
    onSelectProject,
    onOpenCreateProjectModal,
    onCloseCreateProjectModal,
    onCloseDeleteProjectModal,
    onDeleteProject,
    loadingProjects: isLoading,
    isCreateProjectModalVisible,
    isUserSuperAdmin,
    isDeleteProjectModalVisible,
    isDeletingProject,
  };
};
